import React, { useState, useEffect } from "react";
import { useProfile } from "../../../../../../contexts/ProfileContext";
import { nativeLocationTrackingService } from "../../../../../../services/nativeLocationTracking";
import { Edit2, Check, X } from 'lucide-react';
import "./style.css";

export const ProfileDrawer = () => {
  const { 
    profile, 
    loading, 
    updatePreferredName,
    updateBirthDate,
    updateAge,
    updateHeight,
    updateWeight,
    updateGPSPermission
  } = useProfile();
  
  // Editing state for each field
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Start editing a field
  const startEdit = (fieldName, currentValue) => {
    setEditingField(fieldName);
    setEditValue(currentValue || '');
    setError('');
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
    setError('');
  };

  // Save field (routes to individual update methods)
  const saveField = async (fieldName) => {
    setSaving(true);
    setError('');
    
    try {
      switch (fieldName) {
        case 'preferredName':
          await updatePreferredName(editValue);
          break;
        case 'birthdate':
          await updateBirthDate(editValue);
          break;
        case 'age':
          await updateAge(parseInt(editValue));
          break;
        case 'height':
          await updateHeight(editValue);
          break;
        case 'weight':
          await updateWeight(editValue);
          break;
        default:
          throw new Error('Unknown field');
      }
      setEditingField(null);
      setEditValue('');
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // 🆕 GPS Permission Handler (MISSION 20 COMPLIANT - Pure web APIs)
  const handleGPSToggle = async () => {
    try {
      // Check current permission status
      const currentStatus = await nativeLocationTrackingService.checkPermissionStatus();
      
      if (currentStatus === 'granted') {
        // Already granted, toggle off in database
        await updateGPSPermission(false);
        return;
      }
      
      if (currentStatus === 'denied') {
        // Permission denied - try to open settings
        const opened = await nativeLocationTrackingService.openAppSettings();
        
        if (!opened) {
          // WMA or couldn't open settings - show alert
          alert(
            'GPS is blocked. Please enable location access in your browser or device settings for Coach Cedar.'
          );
        }
        // After returning from settings, permission will be checked on focus
        return;
      }
      
      if (currentStatus === 'prompt' || currentStatus === 'unavailable') {
        // Request permission
        const granted = await nativeLocationTrackingService.requestGPSPermission();
        
        // Update database
        await updateGPSPermission(granted);
      }
    } catch (error) {
      console.error('GPS toggle error:', error);
      alert('Failed to update GPS permission. Please try again.');
    }
  };

  // 🆕 Check GPS permission on window focus (when user returns from Settings)
  // MISSION 20 COMPLIANT: Pure web API, no react-native imports
  useEffect(() => {
    const handleFocus = async () => {
      // When user returns to app, check permission status
      const currentStatus = await nativeLocationTrackingService.checkPermissionStatus();
      const isGranted = currentStatus === 'granted';
      
      // Update database if state changed
      if (profile?.gpsPermissionGranted !== isGranted) {
        await updateGPSPermission(isGranted);
      }
    };

    // Use pure web API - works on all platforms
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [profile?.gpsPermissionGranted]); // 🚨 Don't add updateGPSPermission - causes infinite loop!

  // Format display values
  const formatHeight = (height, units) => {
    if (!height) return 'Not set';
    return height;
  };

  const formatWeight = (weight, units) => {
    if (!weight) return 'Not set';
    return weight;
  };

  // Calculate age from birth date
  const getAge = () => {
    if (profile?.age) return profile.age;
    if (profile?.birthdate) {
      const today = new Date();
      const birth = new Date(profile.birthdate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    }
    return null;
  };

  // Format birth date for display
  const formatBirthdate = (birthdate) => {
    if (!birthdate) return 'Not set';
    const date = new Date(birthdate);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="profile-drawer" data-model-id="2279:5003">
        <div className="rectangle" />
        <div className="text-wrapper-3">Loading Profile...</div>
      </div>
    );
  }

  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || 'Not set';
  const subscriptionLabel = profile?.subscriptionStatus === 'beta' ? 'Beta User' : profile?.subscriptionStatus || 'Free';

  return (
    <div className="profile-drawer" data-model-id="2279:5003">
      <div className="rectangle" />

      <div className="text-wrapper-3">Your Profile</div>

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* HEADER SECTION (Non-Editable) */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}

      {/* Full Name */}
      <div className="frame-2 profile-header-section">
        <div className="profile-header-name">
          {fullName}
        </div>
      </div>

      {/* Email */}
      <div className="frame-2 profile-header-section">
        <div className="profile-header-email">
          {profile?.email || 'Not set'}
        </div>
      </div>

      {/* Subscription Level with "modify" link */}
      <div className="frame-2 profile-header-section">
        <div className="profile-subscription">
          Subscription: {subscriptionLabel}{' '}
          <span 
            className="subscription-modify-link"
            onClick={() => alert('Subscription management coming soon')}
          >
            modify
          </span>
        </div>
      </div>

      {/* Spacer */}
      <div style={{ height: '24px' }} />

      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* PROFILE DETAILS (Editable) */}
      {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}

      {/* Preferred Name (Editable) */}
      <div className="frame-2">
        <div className="frame-3">
          <div className="text-wrapper-4">Preferred Name:</div>
        </div>
        <div className="frame-4">
          {editingField === 'preferredName' ? (
            <div className="inline-edit-container">
              <input
                type="text"
                className="inline-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                maxLength={50}
                placeholder="Enter preferred name"
              />
              <button
                className="inline-edit-btn inline-edit-save"
                onClick={() => saveField('preferredName')}
                disabled={saving || !editValue.trim()}
              >
                <Check size={16} />
              </button>
              <button
                className="inline-edit-btn inline-edit-cancel"
                onClick={cancelEdit}
                disabled={saving}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="inline-edit-display" onClick={() => startEdit('preferredName', profile?.preferredName)}>
              <span className={`text-wrapper-5 ${!profile?.preferredName ? 'placeholder' : ''}`}>
                {profile?.preferredName || 'Not set'}
              </span>
              <Edit2 size={16} className="inline-edit-icon" />
            </div>
          )}
        </div>
      </div>
      {error && editingField === 'preferredName' && (
        <div className="inline-edit-error">{error}</div>
      )}

      {/* Birth Date (Editable) */}
      <div className="frame-2">
        <div className="frame-3">
          <div className="text-wrapper-4">Birth Date:</div>
        </div>
        <div className="frame-4">
          {editingField === 'birthdate' ? (
            <div className="inline-edit-container">
              <input
                type="date"
                className="inline-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                max={new Date().toISOString().split('T')[0]}
              />
              <button
                className="inline-edit-btn inline-edit-save"
                onClick={() => saveField('birthdate')}
                disabled={saving || !editValue}
              >
                <Check size={16} />
              </button>
              <button
                className="inline-edit-btn inline-edit-cancel"
                onClick={cancelEdit}
                disabled={saving}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="inline-edit-display" onClick={() => startEdit('birthdate', profile?.birthdate)}>
              <span className={`text-wrapper-5 ${!profile?.birthdate ? 'placeholder' : ''}`}>
                {formatBirthdate(profile?.birthdate)}
              </span>
              <Edit2 size={16} className="inline-edit-icon" />
            </div>
          )}
        </div>
      </div>
      {error && editingField === 'birthdate' && (
        <div className="inline-edit-error">{error}</div>
      )}

      {/* Age (Editable) */}
      <div className="frame-2">
        <div className="frame-3">
          <div className="text-wrapper-4">Age:</div>
        </div>
        <div className="frame-4">
          {editingField === 'age' ? (
            <div className="inline-edit-container">
              <input
                type="number"
                className="inline-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                min={13}
                max={120}
                placeholder="Enter age"
              />
              <button
                className="inline-edit-btn inline-edit-save"
                onClick={() => saveField('age')}
                disabled={saving || !editValue || parseInt(editValue) < 13 || parseInt(editValue) > 120}
              >
                <Check size={16} />
              </button>
              <button
                className="inline-edit-btn inline-edit-cancel"
                onClick={cancelEdit}
                disabled={saving}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="inline-edit-display" onClick={() => startEdit('age', getAge())}>
              <span className={`text-wrapper-5 ${!getAge() ? 'placeholder' : ''}`}>
                {getAge() || 'Not set'}
              </span>
              <Edit2 size={16} className="inline-edit-icon" />
            </div>
          )}
        </div>
      </div>
      {error && editingField === 'age' && (
        <div className="inline-edit-error">{error}</div>
      )}

      {/* Height (Editable) */}
      <div className="frame-2">
        <div className="frame-3">
          <div className="text-wrapper-4">Height:</div>
        </div>
        <div className="frame-4">
          {editingField === 'height' ? (
            <div className="inline-edit-container">
              <input
                type="text"
                className="inline-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                maxLength={20}
                placeholder={profile?.units === 'imperial' ? `e.g., 6'2"` : 'e.g., 188 cm'}
              />
              <button
                className="inline-edit-btn inline-edit-save"
                onClick={() => saveField('height')}
                disabled={saving || !editValue.trim()}
              >
                <Check size={16} />
              </button>
              <button
                className="inline-edit-btn inline-edit-cancel"
                onClick={cancelEdit}
                disabled={saving}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="inline-edit-display" onClick={() => startEdit('height', profile?.height)}>
              <span className={`text-wrapper-5 ${!profile?.height ? 'placeholder' : ''}`}>
                {formatHeight(profile?.height, profile?.units)}
              </span>
              <Edit2 size={16} className="inline-edit-icon" />
            </div>
          )}
        </div>
      </div>
      {error && editingField === 'height' && (
        <div className="inline-edit-error">{error}</div>
      )}

      {/* Weight (Editable) */}
      <div className="frame-2">
        <div className="frame-3">
          <div className="text-wrapper-4">Weight:</div>
        </div>
        <div className="frame-4">
          {editingField === 'weight' ? (
            <div className="inline-edit-container">
              <input
                type="text"
                className="inline-edit-input"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                autoFocus
                maxLength={20}
                placeholder={profile?.units === 'imperial' ? 'e.g., 170 lbs' : 'e.g., 77 kg'}
              />
              <button
                className="inline-edit-btn inline-edit-save"
                onClick={() => saveField('weight')}
                disabled={saving || !editValue.trim()}
              >
                <Check size={16} />
              </button>
              <button
                className="inline-edit-btn inline-edit-cancel"
                onClick={cancelEdit}
                disabled={saving}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="inline-edit-display" onClick={() => startEdit('weight', profile?.weight)}>
              <span className={`text-wrapper-5 ${!profile?.weight ? 'placeholder' : ''}`}>
                {formatWeight(profile?.weight, profile?.units)}
              </span>
              <Edit2 size={16} className="inline-edit-icon" />
            </div>
          )}
        </div>
      </div>
      {error && editingField === 'weight' && (
        <div className="inline-edit-error">{error}</div>
      )}

      {/* Time Zone (Non-Editable, Auto-Detected) */}
      <div className="frame-2">
        <div className="frame-3">
          <div className="text-wrapper-10">Time Zone</div>
        </div>
        <div className="frame-6">
          <div className={`text-wrapper-11 ${!profile?.timezone ? 'placeholder' : ''}`}>
            {profile?.timezone || 'Auto-detected'}
          </div>
        </div>
      </div>

      {/* GPS Tracking (Editable via platform-specific permission) */}
      <div className="frame-2">
        <div className="frame-3">
          <div className="text-wrapper-4">GPS Tracking:</div>
        </div>
        <div className="frame-4">
          <div className="gps-permission-display" onClick={handleGPSToggle}>
            <span className={`text-wrapper-5 ${profile?.gpsPermissionGranted ? 'gps-enabled' : 'gps-disabled'}`}>
              {profile?.gpsPermissionGranted ? 'Enabled' : 'Disabled'}
            </span>
            <Edit2 size={16} className="inline-edit-icon" />
          </div>
        </div>
      </div>
      <div className="gps-permission-hint">
        {profile?.gpsPermissionGranted 
          ? 'GPS access granted for outdoor workouts' 
          : 'Tap to enable GPS for route tracking'}
      </div>

    </div>
  );
};
